import { isNil, startCase } from "lodash";
import { FC, useEffect, useMemo, useRef, useTransition } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import {
  useCrudEditContext,
  useCrudFormCleanup,
  useTableListActions,
} from "@ryde/redux";
import {
  CrudApi,
  currentUserIdSelector,
  editFormDataAF,
  EditFormMode,
  errorsCrudContentAtomFamily,
  formTabControlsSF,
  IDrawersCommonProps,
  IInjectedDrawerProps,
  INotificationDto,
  Log,
  NotificationStateEnum,
  resolveOffsetWidthMultipleDrawers,
  RIGHTBAR_DEFAULT_WIDTH,
  simpleResultFalse,
  simpleResultTrue,
  SINGLE_KEY,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";

import { EditFormTabs, FormControlsWrapper } from "../../../../../layout-edit";
import {
  BaseDrawer,
  DrawerContentStyled,
  DrawerFormContentGate,
  DrawerHeader,
} from "../../../drawer";
import { useEditFormDrawerLoading } from "../../../edit-form";
import { NotificationEditFooter } from "./notification-edit-footer";

export const NotificationEditContent: FC<
  IDrawersCommonProps & IInjectedDrawerProps
> = ({ onCloseDrawer, resolveFn, drawerNumber }) => {
  const { t } = useTranslation();
  const {
    data: { isEdit, dto },
    entity,
    mode,
    id,
  } = useCrudEditContext<INotificationDto>();
  const currentUserId = useRecoilValue(currentUserIdSelector);
  const setDto = useSetRecoilState(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));
  const { resetEditFormPreviousData } = useCrudFormCleanup(
    entity,
    EditFormMode.CrudRoute,
  );
  const currentTabControls = useRecoilValue(formTabControlsSF(entity));
  const { isFormContentLoading } = useEditFormDrawerLoading({
    entity,
    requestedId: id,
    dtoId: dto?.id,
    hasDto: Boolean(dto),
  });
  const [isPending, startTransition] = useTransition();
  const {
    getValues,
    reset,
    formState: { touchedFields, isValid, errors },
  } = useFormContext();
  const { replaceTableDto, removeTableDto } = useTableListActions(entity);
  const isAddNew = !isEdit;
  const unitTitle = t(`${entity}.single`);
  const secondaryTitle = t(`general.ADD_NEW`, { entity: unitTitle });
  const initialIsReadRef = useRef(dto?.isRead);

  // Generate title: "Entity: Name" for edit, "Add new Entity" for adding
  const headerTitle = useMemo(() => {
    if (isEdit && dto?.title) {
      return startCase(`${unitTitle}: ${dto.title}`);
    }
    if (isEdit) {
      return startCase(unitTitle);
    }
    return startCase(secondaryTitle);
  }, [isEdit, dto?.title, unitTitle, secondaryTitle]);

  const isReadValue = getValues("isRead");
  const tagsValue = getValues("metadata.tags");

  const { hasPermissions: hasUpdatePermissions } = useHasPermissions({
    entity,
    action: "update",
  });

  const isPermitToSave =
    hasUpdatePermissions &&
    !isNil(dto?.alertId) &&
    currentUserId === dto.recipientId &&
    dto?.state !== NotificationStateEnum.Resolved;
  const { drawers } = useDrawers();
  const customWidth = resolveOffsetWidthMultipleDrawers({
    customWidth: RIGHTBAR_DEFAULT_WIDTH,
    drawersNumber: 1 + (drawers?.length - 1),
  });

  const closeSidebar = useEvent(() => {
    resetEditFormPreviousData({ skipGlobalDto: true });
    reset(undefined);
    resolveFn?.(simpleResultFalse);
    onCloseDrawer();
  });

  const updateIsRead = useEvent(async (isRead = true) => {
    try {
      const updatedDto = await CrudApi.update<INotificationDto>(
        entity,
        Number(dto?.id),
        {
          isRead,
        },
      );

      if (updatedDto) {
        setDto(updatedDto);
        replaceTableDto(updatedDto as INotificationDto);
      }
    } catch (e: unknown) {
      Log.warnAndToast(t("errors.ERROR_SAVING_DATA"), e);
    }
  });
  const updateTags = useEvent(async tags => {
    try {
      const updatedDto = await CrudApi.update<INotificationDto>(
        entity,
        Number(dto?.id),
        {
          metadata: {
            ...(dto?.metadata || {}),
            tags,
          },
        },
      );

      if (updatedDto) {
        setDto(updatedDto);
        replaceTableDto(updatedDto as INotificationDto);
      }
    } catch (e: unknown) {
      Log.warnAndToast(t("errors.ERROR_SAVING_DATA"), e);
    }
  });

  const onSaveClick = useEvent(async () => {
    try {
      await CrudApi.update(entity, Number(dto?.id), {
        state: NotificationStateEnum.Resolved,
      });
      await CrudApi.delete(entity, Number(dto?.id));

      removeTableDto(Number(dto?.id));
      resetEditFormPreviousData({ skipGlobalDto: true });
      resolveFn?.(simpleResultTrue);
      onCloseDrawer();
    } catch (e) {
      Log.error(
        t("errors.errorArchivingEntityData", {
          entity: t(`${entity}.${SINGLE_KEY}`),
        }),
        e,
      );
    }
  });

  useEffect(() => {
    if (dto?.isRead) {
      initialIsReadRef.current = true;
    }
  }, [dto?.isRead]);

  useEffect(() => {
    if (
      isEdit &&
      dto?.id &&
      !dto?.isRead &&
      !("isRead" in touchedFields) &&
      !initialIsReadRef.current
    ) {
      startTransition(() => {
        updateIsRead();
        initialIsReadRef.current = true;
      });
    }
  }, [dto?.id, dto?.isRead, isEdit, touchedFields, updateIsRead]);
  useEffect(() => {
    if ("isRead" in touchedFields) {
      startTransition(() => {
        updateIsRead(isReadValue);
      });
    }
  }, [isReadValue, updateIsRead, touchedFields.isRead, touchedFields]);
  useEffect(() => {
    if (touchedFields?.metadata?.tags !== undefined) {
      updateTags(tagsValue);
    }
  }, [tagsValue, touchedFields, updateTags]);

  return (
    <BaseDrawer
      onCloseDrawer={closeSidebar}
      drawerNumber={drawerNumber}
      customWidth={`${customWidth}px`}
    >
      <DrawerFormContentGate
        isLoading={isFormContentLoading || isPending}
        entityTitle={unitTitle}
      >
        <DrawerHeader
          onCloseDrawer={closeSidebar}
          title={headerTitle}
          drawerNumber={drawerNumber}
        />
        <DrawerContentStyled className="drawer-form">
          <EditFormTabs>
            <ErrorsDisplayContainer
              serverErrors={serverErrors}
              formErrors={isValid ? undefined : errors}
            />
            {(isAddNew || !!dto) && (
              <FormControlsWrapper controls={currentTabControls} />
            )}
          </EditFormTabs>
        </DrawerContentStyled>
        <NotificationEditFooter
          onCloseDrawer={closeSidebar}
          isSave={isPermitToSave}
          onSave={onSaveClick}
        />
      </DrawerFormContentGate>
    </BaseDrawer>
  );
};
